import React, { useState } from 'react';
import { Form, Input, Row, Col, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import RelatedApprovalDetailModal from './RelatedApprovalDetailModal';
import PrepaymentApplicationReadonlyView from '../ApprovalCenter/PrepaymentApplicationReadonlyView';
import PrepaymentAppendClaimReadonlyView from '../ApprovalCenter/PrepaymentAppendClaimReadonlyView';
import PrepaymentWriteoffReadonlyView from '../ApprovalCenter/PrepaymentWriteoffReadonlyView';
import ExpenseClaimReadonlyView from '../ApprovalCenter/ExpenseClaimReadonlyView';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import {
  getFinanceSnapshotSummaryMeta,
  groupFinanceSnapshotItems
} from '../../utils/expenseFormSnapshotSections';
import {
  isSnapshotFileListItem,
  isSnapshotFullWidthItem,
  isSnapshotTextareaItem,
  resolveSnapshotFiles,
  resolveSnapshotFieldValue,
  isSnapshotCurrencyField
} from './processFormSnapshotFieldUtils';

const { Text } = Typography;

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? '');
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function RelatedApprovalSnapshotField({ item, formData, isHighlight, fieldOptions }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const value = resolveSnapshotFieldValue(item, formData, fieldOptions);
  const valueStr = String(value ?? '');
  const approvalId = formData?.related_approval_id;
  const bizType = formData?.related_approval_biz_type;
  const relatedInboundIds = formData?.related_inbound_ids || [];
  const isMatInbound = bizType === 'mat_inbound';
  const canView = isMatInbound
    ? relatedInboundIds.length > 0
    : Boolean(approvalId && bizType);

  return (
    <Col key={item.key || item.label} xs={24} sm={12}>
      <Form.Item
        label={item.label}
        className={isHighlight ? 'process-form-snapshot-item-highlight' : undefined}
      >
        <Input
          value={valueStr}
          readOnly
          suffix={canView ? (
            <EyeOutlined
              role="button"
              tabIndex={0}
              aria-label="查看关联审批单"
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={() => setDetailOpen(true)}
            />
          ) : null}
        />
      </Form.Item>
      <RelatedApprovalDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        approvalId={approvalId}
        bizType={bizType}
        relatedInboundIds={relatedInboundIds}
      />
    </Col>
  );
}

function SnapshotField({ item, formData, fieldOptions }) {
  const fullWidth = isSnapshotFullWidthItem(item);
  const value = resolveSnapshotFieldValue(item, formData, fieldOptions);
  const valueStr = String(value);
  const isFileList = isSnapshotFileListItem(item);
  const files = isFileList ? resolveSnapshotFiles(item, formData) : [];
  const isHighlight = item.highlight;

  if (item.key === 'related_approval_no') {
    return (
      <RelatedApprovalSnapshotField
        item={item}
        formData={formData}
        isHighlight={isHighlight}
        fieldOptions={fieldOptions}
      />
    );
  }

  return (
    <Col key={item.key || item.label} xs={24} sm={fullWidth ? 24 : 12}>
      <Form.Item
        label={item.label}
        className={isHighlight ? 'process-form-snapshot-item-highlight' : undefined}
      >
        {isFileList && files.length > 0 ? (
          <div className="process-form-snapshot-attachments">
            <SealApprovalAttachmentsView attachments={files} title="" />
          </div>
        ) : isSnapshotTextareaItem(item, valueStr) ? (
          <Input.TextArea
            className="process-form-snapshot-textarea"
            value={valueStr}
            readOnly
            rows={Math.min(10, Math.max(3, valueStr.split('\n').length))}
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        ) : valueStr.length > 60 ? (
          <Input.TextArea
            className="process-form-snapshot-textarea"
            value={valueStr}
            readOnly
            rows={2}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        ) : (
          <Input
            className={isHighlight ? 'process-form-snapshot-input-emphasis' : undefined}
            value={isSnapshotCurrencyField(item) && value !== '' ? formatAmount(value) : valueStr}
            readOnly
          />
        )}
      </Form.Item>
    </Col>
  );
}

function FinanceSnapshotSummary({ items, formData, processType, fieldOptions }) {
  const meta = getFinanceSnapshotSummaryMeta(processType);
  const byKey = new Map((items || []).filter((item) => item?.key).map((item) => [item.key, item]));
  const pick = (key) => resolveSnapshotFieldValue(
    { key, value: byKey.get(key)?.value },
    formData,
    fieldOptions
  );

  const subject = pick('subject');
  const amount = pick(meta.amountKey);
  const amountUpper = pick(meta.uppercaseKey);
  const applicant = pick('applicant_name');
  const dept = pick('applicant_dept_name');

  if (!subject && amount == null && amountUpper == null) return null;

  return (
    <div className="process-form-finance-summary">
      {subject && (
        <div className="process-form-finance-summary-subject">{subject}</div>
      )}
      <div className="process-form-finance-summary-grid">
        {applicant && (
          <div className="process-form-finance-summary-item">
            <Text type="secondary">申请人</Text>
            <span>{applicant}{dept ? ` · ${dept}` : ''}</span>
          </div>
        )}
        {amount != null && amount !== '' && (
          <div className="process-form-finance-summary-item process-form-finance-summary-amount">
            <Text type="secondary">{meta.amountLabel}</Text>
            <span>{formatAmount(amount)}</span>
          </div>
        )}
        {amountUpper && (
          <div className="process-form-finance-summary-item">
            <Text type="secondary">{meta.uppercaseLabel}</Text>
            <span>{amountUpper}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GroupedFinanceFormSnapshotView({
  items = [],
  formData = {},
  processType,
  applicantPrepaymentSummary,
  preferSnapshotValues = false
}) {
  const fieldOptions = { preferFormData: !preferSnapshotValues };
  if (processType === 'exp_prepayment') {
    return (
      <PrepaymentApplicationReadonlyView
        formData={formData}
        applicantPrepaymentSummary={applicantPrepaymentSummary}
      />
    );
  }

  if (processType === 'prepayment_append') {
    return <PrepaymentAppendClaimReadonlyView formData={formData} />;
  }

  if (processType === 'exp_prepayment_writeoff') {
    return <PrepaymentWriteoffReadonlyView formData={formData} />;
  }

  if (processType === 'daily' || processType === 'travel') {
    return <ExpenseClaimReadonlyView formData={formData} processType={processType} />;
  }

  const grouped = groupFinanceSnapshotItems(items, processType, formData);

  return (
    <div className="process-form-finance-grouped">
      <FinanceSnapshotSummary
        items={items}
        formData={formData}
        processType={processType}
        fieldOptions={fieldOptions}
      />
      {grouped.map((section) => (
        <section key={section.title} className="process-form-snapshot-section">
          <h4 className="process-form-snapshot-section-title">{section.title}</h4>
          <Form layout="vertical" className="process-form-readonly process-form-snapshot">
            <Row gutter={[16, 0]}>
              {section.items.map((item) => (
                <SnapshotField
                  key={item.key || item.label}
                  item={section.highlight ? { ...item, highlight: true } : item}
                  formData={formData}
                  fieldOptions={fieldOptions}
                />
              ))}
            </Row>
          </Form>
        </section>
      ))}
    </div>
  );
}
